/**
 * Public German grammar topic page — /:lang/grammar/:level/:slug
 *
 * STYLE: this is GrammarPillarRenderer's lesson layout, not a new design.
 * The dark gradient banner, its 4px #3A86FF underline, the top accent line, the
 * faint grid overlay, the 80px blue rule under the title and the numbered
 * section sidebar are all lifted from the authenticated lesson view
 * (grammarStyles.pillarHeader / GrammarPillarRenderer / ProgressiveSectionReveal).
 *
 * TWO DELIBERATE DEVIATIONS
 *
 * 1. No progressive reveal. The app hides sections behind "Continue" and "Start
 *    Story". Crawlers do not click, and AI crawlers do not run JavaScript at all,
 *    so every section is rendered open and the sidebar is anchor navigation
 *    rather than a stepper. The visual layout is the same; the gating is not.
 *
 * 2. The summary is promoted to the top as an answer block. In the app it is
 *    section 3 of n. 37 of 38 pillars carry a summary of 21-73 words (median 42),
 *    which is exactly the 40-60 word extractable answer that both Google snippets
 *    and AI engines pull. It was written years ago and was simply in the wrong
 *    place.
 *
 * WHAT STAYS PAID
 * `practice` is never rendered as a working exercise. The last section shows one
 * inert sample and the real exercise count, then a sign-up gate — the same
 * preview-then-gate boundary used for Redemittel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChevronLeft, PlayCircle, Lock } from 'lucide-react';
import { useLangParam } from '../hooks/useLocalizedNavigate';
import { renderEmphasis } from '../utils/renderEmphasis';
import { getGrammarTopic, grammarTopicsForLevel, GRAMMAR_LEVEL_LABEL } from '../data/grammarCatalog';
import { useGrammarExercises, exerciseTypeLabel } from '../components/grammar/publicGrammarData';

const BASE_URL = 'https://logami.app';

/* ── header, from grammarStyles.pillarHeader ─────────────────────────────── */
const HEADER = {
  position: 'relative',
  background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
  color: 'white',
  padding: '3rem 2rem',
  textAlign: 'center',
  borderRadius: '0 0 1.5rem 1.5rem',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  borderBottom: '4px solid #3A86FF',
};
const GRID_OVERLAY = {
  position: 'absolute',
  inset: 0,
  backgroundImage: `
    linear-gradient(0deg, transparent 24%, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.03) 26%, transparent 27%, transparent 74%, rgba(255,255,255,0.03) 75%, rgba(255,255,255,0.03) 76%, transparent 77%, transparent),
    linear-gradient(90deg, transparent 24%, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.03) 26%, transparent 27%, transparent 74%, rgba(255,255,255,0.03) 75%, rgba(255,255,255,0.03) 76%, transparent 77%, transparent)`,
  backgroundSize: '50px 50px',
  opacity: 0.4,
  pointerEvents: 'none',
};

const sectionId = (i, name) =>
  `s${i}-${String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;

/* ── section pieces ──────────────────────────────────────────────────────── */
function SectionHeading({ n, children, id }) {
  return (
    <h2 id={id} className="scroll-mt-24 flex items-center gap-3 mb-4">
      <span className="flex-none w-8 h-8 rounded-full bg-black text-white font-space text-sm font-black flex items-center justify-center">
        {n}
      </span>
      <span className="font-space text-xl sm:text-2xl font-black text-black leading-tight">
        {children}
      </span>
    </h2>
  );
}

function Dialogue({ context }) {
  if (!context?.lines?.length) return null;
  return (
    <div className="rounded-2xl border border-[#E1E4E8] bg-white p-5 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
      {context.title && (
        <p className="font-space text-sm font-black uppercase tracking-widest text-black/40 mb-4">
          {context.title}
        </p>
      )}
      {context.lines.map((line, i) => (
        <div key={i} className="mb-5 last:mb-0">
          <p className="text-base text-black leading-relaxed mb-1">
            {line.speaker && <span className="font-space font-bold">{line.speaker}: </span>}
            {renderEmphasis(line.dialogue)}
          </p>
          {line.translation && (
            <p className="text-sm text-gray-500 italic leading-relaxed">
              {renderEmphasis(line.translation)}
            </p>
          )}
          {(line.notes || []).map((note, n) => (
            <div key={n} className="mt-3 rounded-xl bg-gray-50 border border-gray-200 p-4">
              {note.insight?.grammar_point && (
                <p className="font-space text-xs font-black uppercase tracking-widest text-[#3A86FF] mb-1.5">
                  {note.insight.grammar_point}
                </p>
              )}
              {note.insight?.explanation && (
                <p className="text-sm text-gray-700 leading-relaxed">
                  {renderEmphasis(note.insight.explanation)}
                </p>
              )}
              {note.insight?.rule && (
                <p className="mt-2 font-mono text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  {renderEmphasis(note.insight.rule)}
                </p>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function ExamplesTable({ examples }) {
  if (!examples?.length) return null;
  return (
    <div className="overflow-x-auto mt-4 rounded-2xl border border-[#E1E4E8] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
      <table className="w-full sm:min-w-[560px] text-left text-sm">
        <thead className="hidden sm:table-header-group">
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-4 py-3 font-space font-bold text-black">Example</th>
            <th className="px-4 py-3 font-space font-bold text-black">English</th>
            <th className="px-4 py-3 font-space font-bold text-black">Why it works</th>
          </tr>
        </thead>
        <tbody className="block sm:table-row-group sm:divide-y sm:divide-gray-100">
          {examples.map((ex, i) => (
            <tr
              key={i}
              className="block border-b border-gray-200 last:border-b-0 sm:table-row sm:border-b-0 sm:hover:bg-gray-50"
            >
              <td
                data-label="Example"
                className="redemittel-cell block px-4 pt-4 pb-2 align-top sm:table-cell sm:py-3"
              >
                <span className="font-space block font-bold text-black">
                  {renderEmphasis(ex.sentence)}
                </span>
                {ex.context && (
                  <span className="mt-1 block text-xs text-gray-400">{renderEmphasis(ex.context)}</span>
                )}
              </td>
              <td
                data-label="English"
                className="redemittel-cell block px-4 py-1 align-top sm:table-cell sm:py-3"
              >
                <span className="block text-gray-700">{renderEmphasis(ex.translation)}</span>
              </td>
              <td
                data-label="Why it works"
                className="redemittel-cell block px-4 pt-1 pb-4 align-top sm:table-cell sm:py-3"
              >
                <span className="block text-sm text-gray-600 leading-relaxed">
                  {renderEmphasis(ex.analysis)}
                </span>
                {ex.breakdown && (
                  <span className="mt-2 block text-xs text-gray-400 leading-relaxed">
                    {renderEmphasis(ex.breakdown)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── page ────────────────────────────────────────────────────────────────── */
const GrammarTopicPage = () => {
  const { level, slug } = useParams();
  const lang = useLangParam();
  const topic = getGrammarTopic(level, slug);

  const [pillar, setPillar] = useState(null);
  const [failed, setFailed] = useState(false);
  const exercises = useGrammarExercises(topic?.level);
  const bank = topic ? exercises?.[topic.pillarId] : null;

  useEffect(() => {
    if (!topic) return undefined;
    let cancelled = false;
    fetch(`/data/grammar-levels/${topic.level}-grammar_en.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((json) => {
        if (cancelled) return;
        const found = (json.pillars || []).find((p) => p.id === topic.pillarId);
        found ? setPillar(found) : setFailed(true);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [topic]);

  const levelLabel = topic ? GRAMMAR_LEVEL_LABEL[topic.level] : '';
  const canonicalUrl = topic ? `${BASE_URL}/en/grammar/${topic.level}/${topic.slug}` : '';
  const title = topic ? `${topic.title} — German ${levelLabel} Grammar Explained | Logami` : '';
  const summary = pillar?.explanation?.summary || '';
  // Must match scripts/prerender.js exactly. prerender.js bakes the meta tags and
  // prerender-playwright.js restores them over whatever React set, so a different
  // string here would be silently discarded — the same trap BlogPostPage fell into
  // in the other direction. The pillar summary would arguably make a better
  // description, but prerender.js only has the catalog, not the fetched JSON;
  // baking summaries into grammarCatalog.js would be the way to do that.
  const description = topic
    ? `${topic.title} at German ${levelLabel}: the rules, a worked dialogue with translations, and examples with the grammar explained line by line.`
    : '';

  // Section list, mirroring GrammarPillarRenderer's order minus the summary,
  // which is promoted above as the answer block.
  const sections = useMemo(() => {
    if (!pillar) return [];
    const out = [];
    if (pillar.context?.lines?.length) out.push({ name: 'Story in Action', kind: 'story' });
    if (pillar.strategicImportance) out.push({ name: 'Why This Matters', kind: 'importance' });
    (pillar.explanation?.categories || []).forEach((cat) =>
      out.push({ name: cat.name || 'Section', kind: 'category', data: cat })
    );
    out.push({ name: 'Practice Exercises', kind: 'practice', locked: true });
    return out;
  }, [pillar]);

  useEffect(() => {
    if (!topic) return undefined;
    const prevTitle = document.title;
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') || '';
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-grammar-ld', '1');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: topic.title,
          description,
          url: canonicalUrl,
          inLanguage: 'en',
          about: { '@type': 'Thing', name: `German grammar: ${topic.title}` },
          author: { '@type': 'Organization', name: 'Logami', url: BASE_URL },
          publisher: { '@type': 'Organization', name: 'Logami', url: BASE_URL },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'German grammar', item: `${BASE_URL}/en/grammar` },
            {
              '@type': 'ListItem',
              position: 2,
              name: `${levelLabel} grammar`,
              item: `${BASE_URL}/en/grammar/${topic.level}`,
            },
            { '@type': 'ListItem', position: 3, name: topic.title, item: canonicalUrl },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      script.remove();
    };
  }, [topic, title, description, canonicalUrl, levelLabel]);

  if (!topic) return <Navigate to={`/${lang}/grammar`} replace />;

  return (
    <div className="min-h-screen bg-white">
      {/* ── Dark banner, from grammarStyles.pillarHeader ───────────────── */}
      <header style={HEADER}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #3A86FF 0%, #000000 50%, #3A86FF 100%)',
          }}
        />
        <div style={GRID_OVERLAY} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1
            className="font-space uppercase"
            style={{
              fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
              fontWeight: 800,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: '#FFFFFF',
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
              marginBottom: '0.75rem',
            }}
          >
            {topic.title}
          </h1>
          <div
            style={{
              width: '80px',
              height: '4px',
              background: 'linear-gradient(90deg, #3A86FF 0%, #1a5dc7 100%)',
              borderRadius: '2px',
              margin: '0 auto 1rem',
              boxShadow: '0 2px 8px rgba(58,134,255,0.4)',
            }}
          />
          {topic.subtitle && (
            <p
              style={{
                fontSize: '1.125rem',
                opacity: 0.9,
                lineHeight: 1.6,
                maxWidth: '600px',
                margin: '0 auto',
                color: '#E5E7EB',
              }}
            >
              {renderEmphasis(topic.subtitle)}
            </p>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <Link
          to={`/${lang}/grammar/${topic.level}`}
          className="inline-flex items-center gap-1.5 min-h-[44px] font-space text-sm font-bold text-black/50 hover:text-black transition-colors duration-200 mb-6"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          All {levelLabel} grammar
        </Link>

        {failed && <p className="text-gray-600">This topic could not be loaded right now.</p>}
        {!pillar && !failed && (
          <div className="h-64 animate-pulse rounded-2xl border border-[#E1E4E8] bg-gray-50" />
        )}

        {pillar && (
          <div data-prerender-ready="true" className="lg:flex lg:gap-10 lg:items-start">
            {/* ── Sidebar: anchor nav, styled like the app's section stepper ── */}
            <aside className="hidden lg:block flex-none w-64 sticky top-24">
              <nav
                aria-label="On this page"
                className="rounded-2xl border border-[#E1E4E8] bg-white p-4 shadow-[0_4px_20px_rgba(0,0,0,0.05)]"
              >
                <p className="font-space text-[11px] font-black uppercase tracking-widest text-black/40 mb-3">
                  On this page
                </p>
                <ul className="list-none pl-0 m-0 space-y-1">
                  {sections.map((s, i) => (
                    <li key={i}>
                      <a
                        href={`#${sectionId(i + 1, s.name)}`}
                        className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 min-h-[44px] text-sm text-gray-600 hover:bg-gray-50 hover:text-black transition-colors duration-200"
                      >
                        <span className="flex-none w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="leading-snug">{s.name}</span>
                        {s.locked && <Lock className="w-3.5 h-3.5 text-gray-400 ml-auto" aria-hidden="true" />}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <article className="flex-1 min-w-0">
              {/* ── The answer block. Promoted from section 3 to the top. ── */}
              {summary && (
                <div className="rounded-2xl border-l-4 border-[#3A86FF] bg-gray-50 border border-gray-200 p-5 sm:p-6 mb-10">
                  <p className="text-base sm:text-lg text-black leading-relaxed">
                    {renderEmphasis(summary)}
                  </p>
                </div>
              )}

              {sections.map((s, i) => {
                const id = sectionId(i + 1, s.name);
                return (
                  <section key={i} className="mb-12">
                    <SectionHeading n={i + 1} id={id}>
                      {s.name}
                    </SectionHeading>

                    {s.kind === 'story' && <Dialogue context={pillar.context} />}

                    {s.kind === 'importance' && (
                      <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                        {renderEmphasis(pillar.strategicImportance)}
                      </p>
                    )}

                    {s.kind === 'category' && (
                      <>
                        {s.data.rule && (
                          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                            {renderEmphasis(s.data.rule)}
                          </p>
                        )}
                        {s.data.rulesDetail &&
                          typeof s.data.rulesDetail === 'object' &&
                          Object.entries(s.data.rulesDetail).map(([k, v]) =>
                            typeof v === 'string' ? (
                              <p key={k} className="text-base text-gray-700 leading-relaxed mb-2">
                                <span className="font-space font-bold text-black">
                                  {k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim()}:{' '}
                                </span>
                                {renderEmphasis(v)}
                              </p>
                            ) : null
                          )}
                        <ExamplesTable examples={s.data.examples} />
                      </>
                    )}

                    {s.kind === 'practice' && (
                      <div className="rounded-2xl border border-[#006D77] bg-gradient-to-br from-white via-[#f2fbf9] to-[#e7f7f4] p-5 sm:p-6 shadow-[0_8px_26px_rgba(0,109,119,0.18)]">
                        {bank?.sample && (
                          <div className="pointer-events-none select-none mb-5">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">
                                Sample
                              </span>
                              <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">
                                ·
                              </span>
                              <span className="text-[10px] font-bold tracking-widest uppercase text-black/40">
                                {exerciseTypeLabel(bank.sample)}
                              </span>
                            </div>
                            <div className="p-4 bg-white/80 rounded-xl border border-gray-200">
                              <p className="text-sm text-gray-700 leading-snug mb-2">
                                {bank.sample.stimulus || bank.sample.question}
                              </p>
                              {Array.isArray(bank.sample.options) && (
                                <div className="flex flex-wrap gap-1.5">
                                  {bank.sample.options.slice(0, 4).map((o, n) => (
                                    <span
                                      key={n}
                                      className="px-2 py-1 bg-white border border-gray-300 rounded-lg text-xs text-gray-700"
                                    >
                                      {o}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <p className="text-base text-gray-700 leading-relaxed mb-4">
                          {bank?.exerciseCount
                            ? `${bank.exerciseCount} graded exercises on ${topic.title}, with the mistake explained every time you get one wrong.`
                            : `Graded exercises on ${topic.title}, with the mistake explained every time you get one wrong.`}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Link
                            to={`/${lang}/auth?mode=signup`}
                            className="inline-flex items-center justify-center gap-1.5 min-h-[48px] px-6 py-3 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                            style={{ backgroundColor: '#006D77' }}
                          >
                            <PlayCircle className="w-4 h-4" aria-hidden="true" />
                            Start practice free
                          </Link>
                          <Link
                            to={`/${lang}/try`}
                            className="inline-flex items-center justify-center min-h-[48px] px-6 py-3 bg-white text-black border-2 border-black rounded-lg text-sm font-semibold hover:bg-black hover:text-white transition-all"
                          >
                            Take a free mock exam
                          </Link>
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}

              <p className="text-sm text-gray-500">
                {grammarTopicsForLevel(topic.level).length - 1} more {levelLabel} grammar topics —{' '}
                <Link
                  to={`/${lang}/grammar/${topic.level}`}
                  className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
                >
                  see them all
                </Link>
              </p>
            </article>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarTopicPage;
